import { el, svg } from '../ui/dom';

export function duration(milliseconds: number): string {
  const seconds = Math.round(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  return minutes ? `${minutes}m ${seconds % 60}s` : `${seconds}s`;
}

export function dateTime(value: string | null | undefined): string {
  if (!value) return 'Never';
  return new Date(value).toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

export function option(value: string, label: string): HTMLOptionElement {
  return el('option', { text: label, attrs: { value } });
}

export function setSelectOptions(
  select: HTMLSelectElement,
  items: readonly { readonly value: string; readonly label: string }[],
  current = ''
): void {
  select.replaceChildren(...items.map((item) => option(item.value, item.label)));
  select.value = current;
}

export function button(
  label: string,
  variant: 'primary' | 'secondary' | 'danger' | 'quiet' | 'highlight' = 'secondary',
  onClick?: () => void
): HTMLButtonElement {
  return el('button', {
    class: `cms-button cms-button--${variant}`,
    text: label,
    attrs: { type: 'button' },
    on: onClick ? { click: onClick } : undefined,
  });
}

export function input(
  label: string,
  name: string,
  type = 'text',
  value = '',
  required = false
): HTMLLabelElement {
  return el(
    'label',
    { class: 'cms-form-field' },
    el('span', { text: label }),
    el('input', {
      class: 'cms-input',
      attrs: { name, type, value, required, autocomplete: 'off' },
    })
  );
}

export function selectField(
  label: string,
  name: string,
  items: readonly { readonly value: string; readonly label: string }[],
  current = ''
): HTMLLabelElement {
  const select = el('select', { class: 'cms-select', attrs: { name } });
  setSelectOptions(select, items, current);
  return el('label', { class: 'cms-form-field' }, el('span', { text: label }), select);
}

export function formValue(form: HTMLFormElement, name: string): string {
  return String(new FormData(form).get(name) ?? '').trim();
}

export function statusBadge(status: string): HTMLElement {
  return el('span', {
    class: `cms-status-badge cms-status-badge--${status.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`,
    text: status.replaceAll('_', ' '),
  });
}

export function emptyState(title: string, detail: string): HTMLElement {
  return el(
    'div',
    { class: 'cms-empty' },
    el('strong', { text: title }),
    el('p', { text: detail })
  );
}

export function loadingState(label = 'Loading…'): HTMLElement {
  return el('div', { class: 'cms-loading', attrs: { role: 'status' } }, el('span', {}), label);
}

export function icon(name: 'dashboard' | 'students' | 'teachers' | 'schools' | 'admins' | 'audit'): SVGElement {
  const paths: Record<typeof name, string[]> = {
    dashboard: ['M3 10.5 12 3l9 7.5', 'M5 9.5V21h14V9.5', 'M9 21v-7h6v7'],
    students: ['M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2', 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z', 'M22 21v-2a4 4 0 0 0-3-3.87', 'M16 3.13a4 4 0 0 1 0 7.75'],
    teachers: ['M20 7 12 3 4 7l8 4 8-4Z', 'M6 9.5V15c0 2 2.7 4 6 4s6-2 6-4V9.5', 'M20 7v6'],
    schools: ['M3 21h18', 'M5 21V8l7-5 7 5v13', 'M9 21v-5h6v5', 'M8 10h1', 'M15 10h1'],
    admins: ['M12 3 20 7v5c0 4.8-3.2 8-8 9-4.8-1-8-4.2-8-9V7l8-4Z', 'M9 12l2 2 4-5'],
    audit: ['M6 3h12v18H6z', 'M9 8h6', 'M9 12h6', 'm9 16 1.5 1.5L15 15'],
  };
  return svg(
    'svg',
    { viewBox: '0 0 24 24', width: 20, height: 20, fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true' },
    ...paths[name].map((path) => svg('path', { d: path }))
  );
}

export interface DialogControls {
  readonly dialog: HTMLDialogElement;
  readonly body: HTMLElement;
  readonly close: () => void;
}

export function openDialog(title: string, description = '', wide = false): DialogControls {
  const dialog = el('dialog', { class: `cms-dialog${wide ? ' cms-dialog--wide' : ''}` });
  const body = el('div', { class: 'cms-dialog__body' });
  const close = (): void => dialog.close();
  dialog.append(
    el(
      'div',
      { class: 'cms-dialog__surface' },
      el(
        'header',
        { class: 'cms-dialog__header' },
        el('div', {}, el('h2', { text: title }), description ? el('p', { text: description }) : null),
        el('button', {
          class: 'cms-dialog__close',
          text: '×',
          attrs: { type: 'button', 'aria-label': 'Close dialog' },
          on: { click: close },
        })
      ),
      body
    )
  );
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) close();
  });
  dialog.addEventListener('close', () => dialog.remove());
  document.body.append(dialog);
  dialog.showModal();
  return { dialog, body, close };
}

export function errorMessage(reason: unknown): string {
  return reason instanceof Error ? reason.message : 'The request could not be completed.';
}
